import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { AlertCircle, Radio, RefreshCw, X } from "lucide-react";

import ViewContainer from "../../../../../components/layout/view/ViewContainer";
import ViewHeader from "../../../../../components/layout/view/ViewHeader";
import EmptyState from "../../../../../components/feedback/EmptyState";
import RefreshButton from "../../../../../components/buttons/refresh/RefreshButton";
import ContentGrid from "../../../../../components/grids/content/ContentGrid";
import StreamThumbnailCard from "../../../../../components/cards/thumbnails/StreamThumbnailCard";
import SimplePlayer from "../../../../../components/player/SimplePlayer";
import AmazonIVSPlayer from "../../../../../components/streams/AmazonIVSPlayer";

import { useGroupMemberStream } from "../hooks/useGroupMemberStream";

import "./GroupMemberStreamView.css";

const DEFAULT_LIMIT = 12;
const PREVIEW_LIMIT = 5;

const getStatus = (stream) =>
  String(stream?.status || "")
    .trim()
    .toLowerCase();

const getStreamId = (stream) =>
  Number(stream?.streamSessionId ?? stream?.stream_session_id);

const getImage = (stream) =>
  stream?.thumbnailUrl || stream?.thumbnail_url || null;

const getLiveUrl = (stream) =>
  stream?.ivsPlaybackUrl || stream?.ivs_playback_url || "";

const getReplayUrl = (stream) =>
  stream?.vodHlsUrl ||
  stream?.vod_hls_url ||
  stream?.vodMp4Url ||
  stream?.vod_mp4_url ||
  "";

const getErrorMessage = (error) =>
  typeof error === "string"
    ? error
    : error?.message || "Unable to load group streams.";

const formatDate = (value) => {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("en-AU", {
    timeZone: "Australia/Melbourne",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const STATUS_LABELS = {
  live: "Live",
  scheduled: "Scheduled",
  ready: "Replay",
  replay_ready: "Replay",
  ended: "Ended",
  recording_ready: "Recording",
  processing: "Processing",
  failed: "Failed",
};

const getBadgeLabel = (stream) => STATUS_LABELS[getStatus(stream)] || "Stream";

const getBadgeVariant = (stream) => {
  const status = getStatus(stream);

  if (["ready", "replay_ready"].includes(status)) {
    return "replay";
  }

  if (status === "recording_ready") {
    return "recording";
  }

  return STATUS_LABELS[status] ? status : "default";
};

const getDetailText = (stream) => {
  const status = getStatus(stream);

  if (status === "live") return "Streaming now";

  if (status === "scheduled") {
    const date = formatDate(stream?.scheduledStart ?? stream?.scheduled_start);

    return date ? `Scheduled ${date}` : "Scheduled";
  }

  if (["ready", "replay_ready"].includes(status)) {
    return (
      formatDate(
        stream?.actualEnd ??
          stream?.actual_end ??
          stream?.createdAt ??
          stream?.created_at,
      ) || "Replay available"
    );
  }

  const details = {
    ended: "Stream ended",
    recording_ready: "Recording ready",
    processing: "Preparing replay",
    failed: "Stream unavailable",
  };

  return details[status] || "View stream details";
};

const getUnavailableMessage = (stream) => {
  const status = getStatus(stream);

  if (status === "scheduled") {
    const scheduled = formatDate(
      stream?.scheduledStart ?? stream?.scheduled_start,
    );

    return scheduled
      ? `Scheduled for ${scheduled}`
      : "This stream will be available when broadcasting starts.";
  }

  if (status === "ended") {
    return "The stream has ended. A replay is not available yet.";
  }

  if (["recording_ready", "processing"].includes(status)) {
    return "The replay is being prepared.";
  }

  if (status === "failed") {
    return "This stream is currently unavailable.";
  }

  return "This stream is not available yet.";
};

function StreamInfoModal({ stream, watchData, loading, error, onClose }) {
  const effectiveStream = watchData || stream;
  const status = getStatus(effectiveStream);
  const liveUrl = getLiveUrl(watchData);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="group-member-stream-modal"
      role="dialog"
      aria-modal="true"
      aria-label={effectiveStream?.title || "Group stream"}
    >
      <div className="group-member-stream-modal__backdrop" onClick={onClose} />

      <div className="group-member-stream-modal__content">
        <button
          type="button"
          className="group-member-stream-modal__close"
          aria-label="Close stream"
          onClick={onClose}
        >
          <X size={19} />
        </button>

        {loading ? (
          <div className="group-member-stream-modal__message" role="status">
            <RefreshCw
              size={25}
              className="group-member-stream-modal__spinner"
            />
            <h3>Loading stream...</h3>
          </div>
        ) : error ? (
          <div className="group-member-stream-modal__message" role="alert">
            <AlertCircle size={27} />
            <h3>Unable to open stream</h3>
            <p>{getErrorMessage(error)}</p>
          </div>
        ) : status === "live" && liveUrl ? (
          <div className="group-member-stream-modal__player">
            <AmazonIVSPlayer
              key={getStreamId(effectiveStream)}
              playbackUrl={liveUrl}
              autoPlay
              muted={false}
              controls
            />
          </div>
        ) : (
          <div className="group-member-stream-modal__message">
            <Radio size={27} />
            <h3>{getBadgeLabel(effectiveStream)}</h3>
            <p>{getUnavailableMessage(effectiveStream)}</p>
          </div>
        )}

        <div className="group-member-stream-modal__details">
          <h2>{effectiveStream?.title || "Group stream"}</h2>

          {(effectiveStream?.description || effectiveStream?.brandName) && (
            <p>{effectiveStream.description || effectiveStream.brandName}</p>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default function GroupMemberStreamView({
  groupTag,
  limit = DEFAULT_LIMIT,
  preview = false,
  onContentLoaded,
  onPreviewResult,
}) {
  const pageLimit = preview
    ? PREVIEW_LIMIT
    : Math.max(1, Number(limit) || DEFAULT_LIMIT);

  const {
    streams,
    loading,
    loadingMore,
    refreshing,
    hasMore,
    error,
    refresh,
    loadMore,
    selectedStream,
    watchData,
    watchLoading,
    watchError,
    openStream,
    closeStream,
  } = useGroupMemberStream(groupTag, pageLimit);

  const streamList = Array.isArray(streams)
    ? streams.filter((stream) => {
        const id = getStreamId(stream);
        return Number.isSafeInteger(id) && id > 0;
      })
    : [];

  const visibleStreams = preview
    ? streamList.slice(0, PREVIEW_LIMIT)
    : streamList;

  const errorMessage = error ? getErrorMessage(error) : "";
  const isEmpty = !loading && visibleStreams.length === 0;
  const isBusy = loading || loadingMore || refreshing;

  const effectiveStream = watchData || selectedStream;
  const replayUrl = getReplayUrl(watchData);

  const isReplayOpen = Boolean(
    selectedStream &&
    !watchLoading &&
    !watchError &&
    getStatus(effectiveStream) !== "live" &&
    replayUrl,
  );

  const isInfoOpen = Boolean(selectedStream && !isReplayOpen);

  useEffect(() => {
    if (!preview || loading) return;

    const notify = onContentLoaded || onPreviewResult;

    notify?.({
      hasContent: visibleStreams.length > 0,
      error: Boolean(errorMessage),
    });
  }, [
    preview,
    loading,
    visibleStreams.length,
    errorMessage,
    onContentLoaded,
    onPreviewResult,
  ]);

  const handleRefresh = async () => {
    if (isBusy) return;
    await refresh();
  };

  const handleLoadMore = async () => {
    if (preview || isBusy || !hasMore) return;
    await loadMore();
  };

  if (preview && isEmpty && !errorMessage) {
    return null;
  }

  return (
    <>
      <ViewContainer>
        <ViewHeader
          title={`Group Streams (${visibleStreams.length})`}
          description="Streams shared with this group."
        />

        {errorMessage && visibleStreams.length > 0 && (
          <div className="group-member-stream-view__alert" role="alert">
            {errorMessage}
          </div>
        )}

        <div>
          {isEmpty ? (
            !preview && (
              <EmptyState
                title={
                  errorMessage
                    ? "Unable to load streams"
                    : "No streams available"
                }
                description={
                  errorMessage ||
                  "Streams shared with this group will appear here."
                }
              />
            )
          ) : (
            <ContentGrid
              items={visibleStreams}
              loading={loading && !visibleStreams.length}
              columns={4}
              tabletColumns={3}
              mobileColumns={2}
              gap={16}
              showEmpty={false}
              skeletonCount={preview ? PREVIEW_LIMIT : 8}
              renderItem={(stream) => {
                const status = getStatus(stream);
                const canPlay = ["live", "ready", "replay_ready"].includes(
                  status,
                );

                return (
                  <StreamThumbnailCard
                    key={getStreamId(stream)}
                    stream={stream}
                    image={getImage(stream)}
                    badgeLabel={getBadgeLabel(stream)}
                    badgeVariant={getBadgeVariant(stream)}
                    detailText={getDetailText(stream)}
                    isLive={status === "live"}
                    canPlay={canPlay}
                    showPlayButton={canPlay}
                    showMenuButton={false}
                    onClick={openStream}
                  />
                );
              }}
            />
          )}
        </div>

        {!preview && hasMore && visibleStreams.length > 0 && (
          <div className="group-member-stream-view__footer">
            <button
              type="button"
              className="group-member-stream-view__load-more"
              disabled={isBusy}
              onClick={handleLoadMore}
            >
              {loadingMore ? "Loading..." : "Load More"}
            </button>
          </div>
        )}
      </ViewContainer>

      {isInfoOpen && (
        <StreamInfoModal
          stream={selectedStream}
          watchData={watchData}
          loading={watchLoading}
          error={watchError}
          onClose={closeStream}
        />
      )}

      <SimplePlayer
        openModal={isReplayOpen}
        closeModal={closeStream}
        videoUrl={isReplayOpen ? replayUrl : null}
        thumbnail={getImage(effectiveStream) || ""}
        title={effectiveStream?.title || "Stream replay"}
        videoId={getStreamId(effectiveStream) || null}
        shouldTrack={false}
      />
    </>
  );
}
